import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Wallet, Users, Shield, Truck,
  GraduationCap, TrendingUp, LogOut, Activity,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Menu, X, BarChart2, FileText, AlertTriangle, History, Eye, Brain, Settings, Award, Navigation as NavigationIcon} from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { getTabAccess } from '../config/tabAccess';

interface SubItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tabKey: string;
  module: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  subItems?: SubItem[];
  dashboard?: string;
}

export const Navigation = ({ user, onLogout, isCollapsed, onCollapse }) => {
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string,boolean>>({});
  const location = useLocation();
  const { t }    = useTranslation();
  const role     = user?.role;
  const userName = user?.name || user?.username || 'Utilisateur';
  const userRole = user?.role || '';

  const canAccessModule = (dashboard?: string) => {
    if (!dashboard) return true;
    if (role === 'SUPER_ADMIN' || role === 'DG') return true;
    if (role === 'COMMISSAIRE' && ['operations','logistique','formation','reporting'].includes(dashboard)) return true;
    if (role === 'OFFICIER') return true;
    return false;
  };

  const toggleMenu = (id: string) =>
    setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));

  const menuItems: MenuItem[] = [
    {
      id: 'welcome',
      label: t('nav.welcome'),
      icon: Activity,
      path: '/welcome',
    },
    {
      id: 'executive',
      label: t('nav.executive'),
      icon: LayoutDashboard,
      path: '/executive',
      dashboard: 'executive',
    },
    {
      id: 'finance',
      label: t('nav.finance'),
      icon: Wallet,
      dashboard: 'finance',
      subItems: [
        { path: '/finance/intelligence',  label: 'Intelligence Finance',         icon: TrendingUp, tabKey: 'intelligence', module: 'finance' },
        { path: '/finance/advanced',      label: 'Suivi Financier Avancé',  icon: BarChart2,  tabKey: 'advanced',     module: 'finance' },
      ],
    },
    {
      id: 'rh',
      label: t('nav.hr'),
      icon: Users,
      dashboard: 'rh',
      subItems: [
        { path: '/rh/intelligence', label: 'Intelligence RH',      icon: Brain,    tabKey: 'intelligence', module: 'hr' },
        { path: '/rh/advanced',     label: 'Suivi RH Avancé', icon: Shield,   tabKey: 'advanced',     module: 'hr' },
      ],
    },
    {
      id: 'operations',
      label: t('nav.operations'),
      icon: Shield,
      dashboard: 'operations',
      subItems: [
        { path: '/operations/incidents/report', label: 'Déclarer un incident',     icon: FileText,      tabKey: 'report',    module: 'operations' },
        { path: '/operations/smartvision', label: 'SmartVision',           icon: Eye,          tabKey: 'smartvision', module: 'operations' },
        { path: '/operations/incidents/analytics', label: 'Intelligence Incidents', icon: AlertTriangle, tabKey: 'incidents_analytics', module: 'operations' },
      ],
    },
    {
      id: 'logistique',
      label: t('nav.logistics'),
      icon: Truck,
      dashboard: 'logistique',
      subItems: [
        { path: '/logistique/intelligence', label: 'Intelligence Logistique',     icon: Brain,       tabKey: 'intelligence', module: 'logistique' },
        { path: '/logistique/advanced',     label: 'Surveillance Flotte IA',   icon: NavigationIcon, tabKey: 'advanced',     module: 'logistique' },
        { path: '/logistique/assets',    label: 'Gestion des actifs',           icon: Truck,         tabKey: 'assets',    module: 'logistique' },
        { path: '/logistique/gestion',   label: 'Gestion Intelligente',        icon: Settings,       tabKey: 'gestion',   module: 'logistique' },
      ],
    },
    {
      id: 'formation',
      label: t('nav.training'),
      icon: GraduationCap,
      dashboard: 'formation',
      subItems: [
        { path: '/formation/analytics', label: 'Intelligence Formation',     icon: Brain,     tabKey: 'analytics', module: 'formation' },
        { path: '/formation/advanced',  label: 'Suivi Formation Avancé', icon: Award,     tabKey: 'advanced',  module: 'formation' },
      ],
    },
    {
      id: 'reporting',
      label: 'Rapports',
      icon: FileText,
      path: '/reporting/institutional',
      dashboard: 'reporting',
      children: [
        { path: '/reporting/institutional', label: 'Rapports Institutionnels', icon: FileText, tabKey: 'institutional', module: 'reporting' },
      ]
    },
    {
      id: 'lean',
      label: t('nav.lean'),
      icon: TrendingUp,
      dashboard: 'lean',
      subItems: [
        { path: '/lean/analytics', label: 'Intelligence LEAN', icon: Brain, tabKey: 'analytics', module: 'lean' },
      ],
    },
  ];

  const visibleItems = menuItems.filter(item => canAccessModule(item.dashboard));

  React.useEffect(() => {
    visibleItems.forEach(item => {
      if (item.subItems && location.pathname.startsWith('/' + item.id)) {
        setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
      }
    });
  }, [location.pathname]);

  const NavContent = ({ onLinkClick }: { onLinkClick?: () => void }) => (
    <>
      {visibleItems.map(item => {
        const isExpanded    = expandedMenus[item.id];
        const isModuleActive = item.path
          ? location.pathname === item.path
          : location.pathname.startsWith('/' + item.id);

        if (!item.subItems) {
          return (
            <Link
              key={item.id}
              to={item.path!}
              onClick={onLinkClick}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                location.pathname === item.path
                  ? 'bg-gold-500/10 text-gold-500 border border-gold-500/20'
                  : 'text-gray-400 hover:bg-navy-800 hover:text-white'
              } ${isCollapsed ? 'justify-center' : ''}`}
              title={isCollapsed ? item.label : ''}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {!isCollapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        }

        return (
          <div key={item.id}>
            <button
              onClick={() => !isCollapsed && toggleMenu(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                isModuleActive
                  ? 'text-gold-500'
                  : 'text-gray-400 hover:bg-navy-800 hover:text-white'
              } ${isCollapsed ? 'justify-center' : ''}`}
              title={isCollapsed ? item.label : ''}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="text-sm flex-1 text-left">{item.label}</span>
                  {isExpanded
                    ? <ChevronUp className="w-3.5 h-3.5 opacity-60" />
                    : <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  }
                </>
              )}
            </button>

            {!isCollapsed && isExpanded && (
              <div className="ml-4 mt-1 space-y-0.5 border-l border-navy-700 pl-3">
                {item.subItems.map(sub => {
                  const access   = getTabAccess(sub.module, sub.tabKey, role);
                  const isActive = location.pathname === sub.path;
                  const isGrayed = access === 'grayed';

                  if (isGrayed) {
                    return (
                      <div
                        key={sub.path}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg opacity-30 cursor-not-allowed"
                        title="Accès non autorisé pour votre rôle"
                      >
                        <sub.icon className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                        <span className="text-xs text-gray-500">{sub.label}</span>
                        <svg className="w-3 h-3 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-5a3 3 0 100-6 3 3 0 000 6z"/>
                        </svg>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={sub.path}
                      to={sub.path}
                      onClick={onLinkClick}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all ${
                        isActive
                          ? 'bg-gold-500/10 text-gold-500 border border-gold-500/20'
                          : 'text-gray-400 hover:bg-navy-800 hover:text-white'
                      }`}
                    >
                      <sub.icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-xs">{sub.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <>
      {/* ── MOBILE hamburger ── */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-navy-900 border border-navy-700 rounded-lg text-gold-500"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* ── MOBILE backdrop ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)} />
      )}

      {/* ── MOBILE drawer ── */}
      <nav className={`md:hidden fixed left-0 top-0 h-full w-72 bg-navy-900 border-r border-navy-700 flex flex-col z-50 transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-navy-700 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gold-500">NEXUS360</h1>
            <p className="text-xs text-gray-500">DGSN</p>
          </div>
          <button onClick={() => setMobileOpen(false)}
            className="p-1 hover:bg-navy-800 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <NavContent onLinkClick={() => setMobileOpen(false)} />
        </div>
        <div className="p-4 border-t border-navy-700 space-y-2">
          <LanguageSwitcher />
          <div className="px-3 py-2 bg-navy-800 rounded-lg">
            <p className="text-sm text-white">{userName}</p>
            <p className="text-xs text-gray-500">{userRole}</p>
          </div>
          <button onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10">
            <LogOut className="w-5 h-5 shrink-0" />
            <span className="text-sm">{t('nav.logout')}</span>
          </button>
        </div>
      </nav>

      {/* ── DESKTOP sidebar ── */}
      <nav className={`hidden md:flex fixed left-0 top-0 h-full bg-navy-900 border-r border-navy-700 flex-col transition-all duration-300 z-50 ${isCollapsed ? 'w-20' : 'w-72'}`}>
        <div className={`p-6 border-b border-navy-700 flex ${isCollapsed ? 'justify-center' : 'justify-between'} items-center relative`}>
          {!isCollapsed && (
            <div>
              <h1 className="text-xl font-bold text-gold-500">NEXUS360</h1>
              <p className="text-xs text-gray-500">DGSN</p>
            </div>
          )}
          {isCollapsed && <Shield className="w-6 h-6 text-gold-500" />}
          <button
            onClick={() => onCollapse(!isCollapsed)}
            className="p-1 hover:bg-navy-800 rounded-full absolute -right-3 top-20 bg-navy-700 border border-navy-600"
          >
            {isCollapsed
              ? <ChevronRight className="w-4 h-4 text-gold-500" />
              : <ChevronLeft className="w-4 h-4 text-gold-500" />
            }
          </button>
        </div>
        <div className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <NavContent />
        </div>
        <div className="p-4 border-t border-navy-700 space-y-2">
          <LanguageSwitcher />
          <div className={`px-3 py-2 bg-navy-800 rounded-lg ${isCollapsed ? 'text-center' : ''}`}>
            {!isCollapsed
              ? <><p className="text-sm text-white truncate">{userName}</p><p className="text-xs text-gray-500">{userRole}</p></>
              : <div className="flex justify-center"><div className="w-8 h-8 bg-gold-500/20 rounded-full flex items-center justify-center"><span className="text-gold-500 text-xs font-bold">{userName.charAt(0)}</span></div></div>
            }
          </div>
          <button
            onClick={onLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10 ${isCollapsed ? 'justify-center' : ''}`}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span className="text-sm">{t('nav.logout')}</span>}
          </button>
        </div>
      </nav>
    </>
  );
};
// reporting nav fix Fri May 29 23:36:13 WCAST 2026
